import { forwardRef, useImperativeHandle, useRef, useState, type DragEvent } from "react"
import { uploadRefusalMessage } from "@/lib/quotaGate"
import { useNavigate } from "react-router-dom"
import { createCase, createThread, uploadDocuments } from "../api"
import { useBeamWhile } from "../beam"
import { PRIVACY_LINE, PRIVACY_URL } from "../privacy"
import { useReaderContext } from "../readerContext"
import { track } from "@/lib/analytics"
import { AllowanceHint } from "@/features/billing/components/AllowanceHint"
import { GateLine, gateAction, useLimitLine } from "../ds/gate"

const NOTICE_FILE = /\.(pdf|png|jpe?g|webp)$/i
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/*"

export interface NoticeDropHandle { focus: () => void }

const DocIcon = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 3h7l5 5v13H7z" />
    <path d="M14 3v5h5" />
    <path d="M12 17v-6M9 14l3-3 3 3" />
  </svg>
)

/**
 * The composer box once Analyse a notice is chosen: the same frame, now a place to drop the
 * notice. A case file is started first and the notice uploaded into its thread, so the
 * document lands in it; then the confirm step reads it and goes straight on to the reading.
 */
const COPY = {
  notice: {
    drop: "Drop the notice here",
    kinds: "A DRC-01, SCN, DRC-01A or ASMT-10 · PDF or a phone photo",
    then: "Nira reads it and works out the time limit, the allegations and the money. Nothing about who you are is asked.",
    label: "The notice to analyse",
  },
  order: {
    drop: "Drop the order here",
    kinds: "An Order-in-Original, Order-in-Appeal or DRC-07 · PDF or a phone photo",
    then: "Nira reads the order: the appeal-by date, the pre-deposit, the forum and the points its own text gives.",
    label: "The order to read for an appeal",
  },
}

export const NoticeDrop = forwardRef<NoticeDropHandle, { onAsk: () => void; kind?: "notice" | "order" }>(function NoticeDrop({ onAsk, kind = "notice" }, handle) {
  const copy = COPY[kind]
  const navigate = useNavigate()
  const input = useRef<HTMLInputElement>(null)
  const choose = useRef<HTMLButtonElement>(null)
  const [state, setState] = useState<"" | "busy" | "bad" | "failed">("")
  const [refusal, setRefusal] = useState("")
  const [over, setOver] = useState(false)
  const [progress, setProgress] = useState("")
  // The second notice (or order) is Pro; Pro's own daily limit just says when it resets. Both before anything is uploaded or read.
  const metric = kind === "order" ? "nira.appeal_reading" : "nira.notice_reading"
  const limitLine = useLimitLine(metric)
  const [paused, setPaused] = useState("")
  useImperativeHandle(handle, () => ({ focus: () => choose.current?.focus() }))
  useBeamWhile("listening", over || state === "busy")

  const take = async (file: File | undefined) => {
    if (!file || state === "busy") return
    if (!NOTICE_FILE.test(file.name)) { setState("bad"); return }
    setPaused("")
    if (limitLine) return
    const gate = await gateAction(metric)
    if (!gate.ok) { setPaused(gate.line); return }
    setState("busy")
    setRefusal("")
    track("notice_dropped", { door: kind, type: /\.pdf$/i.test(file.name) ? "pdf" : "image" })
    try {
      const thread = await createThread(undefined, useReaderContext.getState().preferred)
      const matter = await createCase({ thread_id: thread.id })
      track("matter_created", { from: "notice" })
      await uploadDocuments(thread.id, [file], setProgress)
      navigate(`/case/${encodeURIComponent(matter.id)}/confirm${kind === "order" ? "?for=appeal" : ""}`)
    } catch (err) {
      setRefusal(uploadRefusalMessage(err) ?? "")
      setState("failed")
    }
  }
  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setOver(false)
    void take(e.dataTransfer.files[0])
  }

  return (
    <div className="nl-sz">
      <div
        className={`nl-lbox nh-drop${over ? " over" : ""}`}
        data-busy={state === "busy" || undefined}
        onDragOver={(e) => { e.preventDefault(); setOver(true) }}
        onDragLeave={() => setOver(false)}
        onDrop={onDrop}
      >
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          hidden
          tabIndex={-1}
          aria-label={copy.label}
          onChange={(e) => { void take(e.target.files?.[0]); e.target.value = "" }}
        />
        {DocIcon}
        <p className="nh-drop-t">{state === "busy" ? `Uploading the ${kind}…` : over ? "Let go to read it" : copy.drop}</p>
        <p className="nh-drop-s" aria-live="polite">
          {state === "busy" && progress ? progress : copy.kinds}
        </p>
        <button ref={choose} type="button" className="nh-drop-b" disabled={state === "busy"} onClick={() => input.current?.click()}>
          Choose the file
        </button>
      </div>
      <div className="nl-help">
        <p className="nl-help-t">{copy.then}</p>
        <button type="button" className="nh-drop-ask" onClick={onAsk}>Ask a question instead</button>
      </div>
      <p className="nh-drop-mask">
        {PRIVACY_LINE}{" "}
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer">What Nira does with your documents</a>
      </p>
      <AllowanceHint metric={metric} className="ngl-hint" />
      <GateLine text={limitLine || paused} />
      <p className={`nl-note${state === "bad" || state === "failed" ? " show" : ""}`} role="status">
        {state === "bad" ? `Use a PDF or a photo of the ${kind}.` : state === "failed" ? (refusal || "Couldn't upload it. Try again.") : ""}
      </p>
    </div>
  )
})
